import {
  action,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx'

import {
  IConditionalField,
  IDailyLogFieldMappingInput,
  IIntegrationWorkflowTypeConfigInput,
  IPermitFieldSourceInput,
  IPermitType,
  IPermitTypeField,
  IWorkflowStep,
  PermitFieldType,
  SitePermitSpecialFieldType,
} from '~/client/graph/types.generated'
import {
  MAX_CONDITIONAL_DEPTH_LEVEL,
  PERMIT_TABLE_FIELDS_KEY,
} from '~/client/src/shared/constants/permitTypeFieldsConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IProcoreProject } from '~/client/src/shared/services/ProcoreService'
import ProcoreIntegrationStore from '~/client/src/shared/stores/domain/ProcoreStores/ProcoreIntegration.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'

export interface IWorkflowField {
  id: string
  caption: string
  type: PermitFieldType | null
  stepPosition?: number
  fieldPosition?: number
  subFieldPosition?: number
  nestedSubFieldPositions?: number[]
  shouldIndent?: boolean
  indentLevel?: number
  isConditional?: boolean
  isDisabled?: boolean
  parentFieldId?: string
}

const SPECIAL_FIELD_PREFIX = 'special_'

const SPECIAL_FIELDS: Array<{
  id: string
  caption: string
  enumValue: SitePermitSpecialFieldType
}> = [
  {
    id: `${SPECIAL_FIELD_PREFIX}workflowid`,
    caption: 'Workflow ID',
    enumValue: SitePermitSpecialFieldType.WorkflowId,
  },
  {
    id: `${SPECIAL_FIELD_PREFIX}workflowlog`,
    caption: 'Workflow Log',
    enumValue: SitePermitSpecialFieldType.WorkflowLog,
  },
  {
    id: `${SPECIAL_FIELD_PREFIX}logattachments`,
    caption: 'Log Attachments',
    enumValue: SitePermitSpecialFieldType.LogAttachments,
  },
]

const EXCLUDED_FIELD_TYPES = [
  PermitFieldType.Section,
  PermitFieldType.Divider,
  PermitFieldType.Subsection,
  PermitFieldType.Instructions,
  PermitFieldType.Text,
]

interface IProcoreDailyLogField {
  name: string
  displayName: string
}

const isDateTimePermitField = (fieldType: PermitFieldType | null): boolean => {
  if (!fieldType) return false
  return (
    fieldType === PermitFieldType.StartFinishDates ||
    fieldType === PermitFieldType.AdditionalDate
  )
}

const isStringPermitField = (fieldType: PermitFieldType | null): boolean => {
  if (!fieldType) return false
  return (
    fieldType === PermitFieldType.Select ||
    fieldType === PermitFieldType.InputText ||
    fieldType === PermitFieldType.InputTextArea ||
    fieldType === PermitFieldType.InputNumber ||
    fieldType === PermitFieldType.Email ||
    fieldType === PermitFieldType.PhoneNumber ||
    fieldType === PermitFieldType.Time
  )
}

const isObjectIdPermitField = (fieldType: PermitFieldType | null): boolean => {
  if (!fieldType) return false
  return (
    fieldType === PermitFieldType.Company ||
    fieldType === PermitFieldType.CompanyInformational ||
    fieldType === PermitFieldType.Requester ||
    fieldType === PermitFieldType.Assignee ||
    fieldType === PermitFieldType.Subscriber ||
    fieldType === PermitFieldType.User ||
    fieldType === PermitFieldType.LinkedWorkflow ||
    fieldType === PermitFieldType.Activity ||
    fieldType === PermitFieldType.PrimaryActivity
  )
}

const isAttachmentPermitField = (
  fieldType: PermitFieldType | null,
): boolean => {
  if (!fieldType) return false
  return fieldType === PermitFieldType.File
}

const isChecklistAnswerItemPermitField = (
  fieldType: PermitFieldType | null,
): boolean => {
  if (!fieldType) return false
  return (
    fieldType === PermitFieldType.Checklist ||
    fieldType === PermitFieldType.Question
  )
}

const isRichTextField = (fieldType: PermitFieldType | null): boolean => {
  if (!fieldType) return false
  return fieldType === PermitFieldType.RichText
}

const isFieldAllowedForProcoreField = (
  field: IWorkflowField,
  procoreFieldName: string,
): boolean => {
  const { type, id, isDisabled } = field

  if (isDisabled) return true

  if (id.startsWith(SPECIAL_FIELD_PREFIX)) {
    const specialFieldId = id

    switch (procoreFieldName) {
      case 'time':
        return false
      case 'delivery_from':
        return false
      case 'tracking_number':
        return specialFieldId === `${SPECIAL_FIELD_PREFIX}workflowid`
      case 'contents':
        return false
      case 'comments':
        return specialFieldId === `${SPECIAL_FIELD_PREFIX}workflowlog`
      case 'attachments':
        return specialFieldId === `${SPECIAL_FIELD_PREFIX}logattachments`
      default:
        return false
    }
  }

  switch (procoreFieldName) {
    case 'time':
      return isDateTimePermitField(type)
    case 'delivery_from':
      return isObjectIdPermitField(type)
    case 'tracking_number':
      return isStringPermitField(type)
    case 'contents':
      return (
        !isAttachmentPermitField(type) &&
        !isRichTextField(type) &&
        !isChecklistAnswerItemPermitField(type)
      )
    case 'comments':
      return isRichTextField(type) || isChecklistAnswerItemPermitField(type)
    case 'attachments':
      return isAttachmentPermitField(type)
    default:
      return false
  }
}

const PROCORE_FIELD_SPECIAL_FIELDS_MAPPING: Record<string, string[] | null> = {
  time: [],
  delivery_from: [],
  tracking_number: [`${SPECIAL_FIELD_PREFIX}workflowid`],
  contents: [`${SPECIAL_FIELD_PREFIX}workflowid`],
  comments: [`${SPECIAL_FIELD_PREFIX}workflowlog`],
  attachments: [`${SPECIAL_FIELD_PREFIX}logattachments`],
}

export default class ProcoreIntegrationSectionStore {
  @observable public isProjectDropdownShown: boolean = false
  @observable public isCompanyDropdownShown: boolean = false
  @observable public searchQuery: string = ''
  @observable public companySearchQuery: string = ''
  @observable public selectedWorkflowTypeIds: string[] = []
  @observable public workflowTypeFieldMappings: Map<
    string,
    IDailyLogFieldMappingInput[]
  > = new Map()
  @observable public openFieldSelectorKey: string | null = null
  @observable public openWorkflowMenuId: string | null = null
  @observable public workflowMappingTables: string[] = []
  @observable public openWorkflowTypeDropdownIndex: number | null = null

  private workflowTypeFieldsCache: Map<string, IWorkflowField[]> = new Map()
  private procoreDailyLogFieldsCache: IProcoreDailyLogField[] | null = null
  private configReactionDisposer: IReactionDisposer | null = null
  private lastInitializedWorkflowTypesJson: string = ''

  public constructor(
    private readonly procoreIntegrationStore: ProcoreIntegrationStore,
    private readonly workflowTypesStore?: WorkflowTypesStore,
  ) {
    makeObservable(this)
    this.initializeWorkflowTypesFromConfig()
    this.setupConfigReaction()
  }

  private setupConfigReaction() {
    this.configReactionDisposer = reaction(
      () => this.procoreIntegrationStore.procoreConfiguration?.workflowTypes,
      workflowTypes => {
        const currentJson = JSON.stringify(workflowTypes || [])
        if (
          workflowTypes &&
          workflowTypes.length > 0 &&
          currentJson !== this.lastInitializedWorkflowTypesJson
        ) {
          this.initializeWorkflowTypesFromConfig()
        }
      },
    )
  }

  public dispose() {
    this.configReactionDisposer?.()
  }

  @action.bound
  public initializeWorkflowTypesFromConfig() {
    const workflowTypes =
      this.procoreIntegrationStore.procoreConfiguration?.workflowTypes
    if (!workflowTypes || !Array.isArray(workflowTypes)) {
      return
    }

    this.lastInitializedWorkflowTypesJson = JSON.stringify(workflowTypes)

    let hasResolvedStaleTypes = false
    const resolvedTypeIds: string[] = []

    workflowTypes.forEach(workflowType => {
      const { resolvedTypeId, wasStale } =
        this.resolveWorkflowTypeId(workflowType)
      if (wasStale) hasResolvedStaleTypes = true

      if (wasStale && workflowType.typeId) {
        const staleId = workflowType.typeId
        this.selectedWorkflowTypeIds = this.selectedWorkflowTypeIds.filter(
          id => id !== staleId,
        )
        this.workflowTypeFieldMappings.delete(staleId)

        const staleTableIndex = this.workflowMappingTables.indexOf(staleId)
        if (staleTableIndex !== -1) {
          this.workflowMappingTables[staleTableIndex] = resolvedTypeId
        }
      }

      if (resolvedTypeId) {
        resolvedTypeIds.push(resolvedTypeId)

        if (!this.selectedWorkflowTypeIds.includes(resolvedTypeId)) {
          this.selectedWorkflowTypeIds.push(resolvedTypeId)
        }

        this.workflowTypeFieldMappings.set(
          resolvedTypeId,
          workflowType.fieldMappings || [],
        )
      }
    })

    if (this.workflowMappingTables.length === 0 && resolvedTypeIds.length > 0) {
      this.workflowMappingTables.push(...resolvedTypeIds)
    }

    if (hasResolvedStaleTypes) {
      this.saveWorkflowTypeMappings().catch(error => {
        console.error('Failed to auto-save resolved workflow types:', error)
      })
    }

    if (this.workflowMappingTables.length === 0) {
      this.workflowMappingTables.push('')
    }
  }

  @action.bound
  public toggleProjectDropdown() {
    this.isProjectDropdownShown = !this.isProjectDropdownShown
  }

  @action.bound
  public toggleCompanyDropdown() {
    this.isCompanyDropdownShown = !this.isCompanyDropdownShown
  }

  @action.bound
  public closeProjectDropdown() {
    this.isProjectDropdownShown = false
  }

  @action.bound
  public closeCompanyDropdown() {
    this.isCompanyDropdownShown = false
  }

  @action.bound
  public openWorkflowTypeDropdown(index: number) {
    this.openWorkflowTypeDropdownIndex = index
  }

  @action.bound
  public closeWorkflowTypeDropdown() {
    this.openWorkflowTypeDropdownIndex = null
  }

  @action.bound
  public setSearchQuery(query: string) {
    this.searchQuery = query
  }

  @action.bound
  public setCompanySearchQuery(query: string) {
    this.companySearchQuery = query
  }

  @action.bound
  public handleWorkflowTypeSelect = async (workflowTypeId: string) => {
    const isNewSelection =
      !this.selectedWorkflowTypeIds.includes(workflowTypeId)

    if (isNewSelection) {
      this.selectedWorkflowTypeIds.push(workflowTypeId)
      this.workflowTypeFieldMappings.set(workflowTypeId, [])
    }
    this.closeWorkflowTypeDropdown()

    if (isNewSelection) {
      try {
        await this.autoSelectTimeField(workflowTypeId, true)
        await this.autoSelectTrackingNumberField(workflowTypeId, true)
        await this.saveWorkflowTypeMappings()
      } catch (error) {
        console.error('Failed to save workflow type mappings:', error)
      }
    }
  }

  @action.bound
  public removeWorkflowType = async (workflowTypeId: string) => {
    this.selectedWorkflowTypeIds = this.selectedWorkflowTypeIds.filter(
      id => id !== workflowTypeId,
    )
    this.workflowTypeFieldMappings.delete(workflowTypeId)

    try {
      await this.saveWorkflowTypeMappings()
    } catch (error) {
      console.error('Failed to save workflow type mappings:', error)
    }
  }

  public getProcoreDailyLogFields(): IProcoreDailyLogField[] {
    if (this.procoreDailyLogFieldsCache) {
      return this.procoreDailyLogFieldsCache
    }

    this.procoreDailyLogFieldsCache = [
      {
        name: 'time',
        displayName: Localization.translator.procoreFieldMappings.time,
      },
      {
        name: 'tracking_number',
        displayName:
          Localization.translator.procoreFieldMappings.trackingNumber,
      },
      {
        name: 'delivery_from',
        displayName: Localization.translator.procoreFieldMappings.deliveryFrom,
      },
      {
        name: 'contents',
        displayName: Localization.translator.procoreFieldMappings.contents,
      },
      {
        name: 'comments',
        displayName: Localization.translator.procoreFieldMappings.comments,
      },
      {
        name: 'attachments',
        displayName: Localization.translator.procoreFieldMappings.attachments,
      },
    ]
    return this.procoreDailyLogFieldsCache
  }

  public isMultipleSelectionField(dailyLogFieldName: string): boolean {
    return ['contents', 'attachments'].includes(dailyLogFieldName.toLowerCase())
  }

  public isFieldAllowedForProcoreField(
    field: IWorkflowField,
    procoreFieldName: string,
  ): boolean {
    return isFieldAllowedForProcoreField(field, procoreFieldName)
  }

  public getAllowedSpecialFieldIds(procoreFieldName: string): string[] | null {
    return PROCORE_FIELD_SPECIAL_FIELDS_MAPPING[procoreFieldName] ?? null
  }

  public getFilteredSelectableFields(
    workflowFields: IWorkflowField[],
    procoreFieldName: string,
  ): IWorkflowField[] {
    if (!workflowFields || workflowFields.length === 0) return []

    const passesFilter = (field: IWorkflowField): boolean => {
      if (field.isDisabled) return false
      return this.isFieldAllowedForProcoreField(field, procoreFieldName)
    }

    const filteredFields = workflowFields.filter(field => {
      if (field.isDisabled) {
        const hasValidColumn = workflowFields.some(
          f => f.parentFieldId === field.id && !f.isDisabled && passesFilter(f),
        )
        return hasValidColumn
      }
      return this.isFieldAllowedForProcoreField(field, procoreFieldName)
    })

    return filteredFields.filter(f => !f.isDisabled)
  }

  @action.bound
  public async autoSelectTimeField(workflowTypeId: string, skipSave = false) {
    const existingMappings =
      this.workflowTypeFieldMappings.get(workflowTypeId) || []
    const hasTimeMapping = existingMappings.some(
      m =>
        m.dailyLogFieldName === 'time' &&
        m.fieldSources &&
        m.fieldSources.length > 0,
    )

    if (hasTimeMapping) return

    const permitType =
      this.workflowTypesStore?.getPermitTypeById(workflowTypeId)
    if (!permitType) return

    const workflowFields = this.getWorkflowTypeFields(permitType)
    if (workflowFields.length === 0) return

    const firstTimeField = workflowFields.find(field =>
      isDateTimePermitField(field.type),
    )
    if (!firstTimeField) return

    const fieldSource = this.createFieldSource(workflowTypeId, firstTimeField)
    if (!fieldSource) return

    await this.updateWorkflowTypeFieldMapping(
      workflowTypeId,
      'time',
      [fieldSource],
      skipSave,
    )
  }

  @action.bound
  public async autoSelectTrackingNumberField(
    workflowTypeId: string,
    skipSave = false,
  ) {
    const existingMappings =
      this.workflowTypeFieldMappings.get(workflowTypeId) || []
    const hasTrackingNumberMapping = existingMappings.some(
      m =>
        m.dailyLogFieldName === 'tracking_number' &&
        m.fieldSources &&
        m.fieldSources.length > 0,
    )

    if (hasTrackingNumberMapping) return

    const workflowIdSpecialField = SPECIAL_FIELDS.find(
      f => f.enumValue === SitePermitSpecialFieldType.WorkflowId,
    )
    if (!workflowIdSpecialField) return

    const fieldSource: IPermitFieldSourceInput = {
      permitSpecialField: workflowIdSpecialField.enumValue,
    }

    await this.updateWorkflowTypeFieldMapping(
      workflowTypeId,
      'tracking_number',
      [fieldSource],
      skipSave,
    )
  }

  @action.bound
  public updateWorkflowTypeFieldMapping = async (
    workflowTypeId: string,
    dailyLogFieldName: string,
    fieldSources: IPermitFieldSourceInput[],
    skipSave = false,
  ) => {
    const currentMappings =
      this.workflowTypeFieldMappings.get(workflowTypeId) || []
    const existingIndex = currentMappings.findIndex(
      m => m.dailyLogFieldName === dailyLogFieldName,
    )

    const newMapping: IDailyLogFieldMappingInput = {
      dailyLogFieldName,
      fieldSources: fieldSources.length > 0 ? fieldSources : undefined,
    }

    let updatedMappings: IDailyLogFieldMappingInput[]

    if (existingIndex >= 0) {
      updatedMappings = [...currentMappings]
      updatedMappings[existingIndex] = newMapping
    } else {
      updatedMappings = [...currentMappings, newMapping]
    }

    this.workflowTypeFieldMappings.set(workflowTypeId, updatedMappings)

    if (!skipSave) {
      try {
        await this.saveWorkflowTypeMappings()
      } catch (error) {
        console.error('Failed to save workflow type mappings:', error)
      }
    }
  }

  @action.bound
  public addWorkflowTypeFieldMapping = async (
    workflowTypeId: string,
    dailyLogFieldName: string,
    fieldSource: IPermitFieldSourceInput,
  ) => {
    const currentMappings =
      this.workflowTypeFieldMappings.get(workflowTypeId) || []
    const existingMappingIndex = currentMappings.findIndex(
      m => m.dailyLogFieldName === dailyLogFieldName,
    )

    let updatedMappings: IDailyLogFieldMappingInput[]

    if (existingMappingIndex >= 0) {
      const existingMapping = currentMappings[existingMappingIndex]
      const existingSources = existingMapping.fieldSources || []
      const isDuplicate = existingSources.some(source =>
        this.areFieldSourcesEqual(source, fieldSource),
      )

      if (!isDuplicate) {
        updatedMappings = [...currentMappings]
        updatedMappings[existingMappingIndex] = {
          ...existingMapping,
          fieldSources: [...existingSources, fieldSource],
        }
      } else {
        return
      }
    } else {
      updatedMappings = [
        ...currentMappings,
        {
          dailyLogFieldName,
          fieldSources: [fieldSource],
        },
      ]
    }

    this.workflowTypeFieldMappings.set(workflowTypeId, updatedMappings)

    try {
      await this.saveWorkflowTypeMappings()
    } catch (error) {
      console.error('Failed to save workflow type mappings:', error)
    }
  }

  @action.bound
  public removeWorkflowTypeFieldMapping = async (
    workflowTypeId: string,
    dailyLogFieldName: string,
    fieldSource: IPermitFieldSourceInput,
  ) => {
    const currentMappings =
      this.workflowTypeFieldMappings.get(workflowTypeId) || []
    const existingMappingIndex = currentMappings.findIndex(
      m => m.dailyLogFieldName === dailyLogFieldName,
    )

    if (existingMappingIndex < 0) return

    const existingMapping = currentMappings[existingMappingIndex]
    if (!existingMapping.fieldSources) return

    const filteredSources = existingMapping.fieldSources.filter(
      source => !this.areFieldSourcesEqual(source, fieldSource),
    )

    let updatedMappings: IDailyLogFieldMappingInput[]

    if (filteredSources.length === 0) {
      updatedMappings = currentMappings.filter(
        (_, index) => index !== existingMappingIndex,
      )
    } else {
      updatedMappings = [...currentMappings]
      updatedMappings[existingMappingIndex] = {
        ...existingMapping,
        fieldSources: filteredSources,
      }
    }

    this.workflowTypeFieldMappings.set(workflowTypeId, updatedMappings)

    try {
      await this.saveWorkflowTypeMappings()
    } catch (error) {
      console.error('Failed to save workflow type mappings:', error)
    }
  }

  public getWorkflowTypeFieldMapping(
    workflowTypeId: string,
    dailyLogFieldName: string,
  ): IPermitFieldSourceInput[] | undefined {
    const mappings = this.workflowTypeFieldMappings.get(workflowTypeId)
    return mappings?.find(m => m.dailyLogFieldName === dailyLogFieldName)
      ?.fieldSources
  }

  @action.bound
  public toggleFieldSelector(
    workflowTypeId: string,
    dailyLogFieldName: string,
  ) {
    const key = `${workflowTypeId}_${dailyLogFieldName}`
    this.openFieldSelectorKey = this.openFieldSelectorKey === key ? null : key
  }

  @action.bound
  public closeFieldSelector() {
    this.openFieldSelectorKey = null
  }

  @action.bound
  public toggleWorkflowMenu(workflowTypeId: string) {
    this.openWorkflowMenuId =
      this.openWorkflowMenuId === workflowTypeId ? null : workflowTypeId
  }

  @action.bound
  public closeWorkflowMenu() {
    this.openWorkflowMenuId = null
  }

  public isWorkflowMenuOpen(workflowTypeId: string): boolean {
    return this.openWorkflowMenuId === workflowTypeId
  }

  @action.bound
  public addWorkflowMappingTable() {
    this.workflowMappingTables.push('')
  }

  @action.bound
  public async removeWorkflowMappingTable(index: number) {
    const workflowTypeId = this.workflowMappingTables[index]

    this.closeWorkflowMenu()
    this.workflowMappingTables.splice(index, 1)

    if (workflowTypeId) {
      this.selectedWorkflowTypeIds = this.selectedWorkflowTypeIds.filter(
        id => id !== workflowTypeId,
      )
      this.workflowTypeFieldMappings.delete(workflowTypeId)

      try {
        await this.saveWorkflowTypeMappings()
      } catch (error) {
        console.error('Failed to save workflow type mappings:', error)
      }
    }
  }

  @action.bound
  public async setWorkflowTypeForTable(index: number, workflowTypeId: string) {
    const oldWorkflowTypeId = this.workflowMappingTables[index]

    if (oldWorkflowTypeId && oldWorkflowTypeId !== workflowTypeId) {
      const isUsedElsewhere = this.workflowMappingTables.some(
        (id, idx) => idx !== index && id === oldWorkflowTypeId,
      )

      if (!isUsedElsewhere) {
        this.selectedWorkflowTypeIds = this.selectedWorkflowTypeIds.filter(
          id => id !== oldWorkflowTypeId,
        )
        this.workflowTypeFieldMappings.delete(oldWorkflowTypeId)
      }
    }

    this.workflowMappingTables[index] = workflowTypeId

    if (!this.selectedWorkflowTypeIds.includes(workflowTypeId)) {
      this.selectedWorkflowTypeIds.push(workflowTypeId)
    }

    if (!this.workflowTypeFieldMappings.has(workflowTypeId)) {
      this.workflowTypeFieldMappings.set(workflowTypeId, [])
    }

    try {
      await this.autoSelectTimeField(workflowTypeId, true)
      await this.autoSelectTrackingNumberField(workflowTypeId, true)
      await this.saveWorkflowTypeMappings()
    } catch (error) {
      console.error('Failed to set workflow type for table:', error)
    }
  }

  public isFieldSelectorOpen(
    workflowTypeId: string,
    dailyLogFieldName: string,
  ): boolean {
    const key = `${workflowTypeId}_${dailyLogFieldName}`
    return this.openFieldSelectorKey === key
  }

  public getWorkflowTypesForSave():
    | IIntegrationWorkflowTypeConfigInput[]
    | undefined {
    const workflowTypes: IIntegrationWorkflowTypeConfigInput[] = []

    this.selectedWorkflowTypeIds.forEach(workflowTypeId => {
      const fieldMappings =
        this.workflowTypeFieldMappings.get(workflowTypeId) || []
      const validMappings = fieldMappings.filter(
        m =>
          m.fieldSources &&
          Array.isArray(m.fieldSources) &&
          m.fieldSources.length > 0,
      )

      const permitType =
        this.workflowTypesStore?.getPermitTypeById(workflowTypeId)

      workflowTypes.push({
        typeId: workflowTypeId,
        initialType: permitType?.type || undefined,
        fieldMappings: validMappings.length > 0 ? validMappings : undefined,
      })
    })

    return workflowTypes.length > 0 ? workflowTypes : undefined
  }

  public async saveWorkflowTypeMappings() {
    const workflowTypes = this.getWorkflowTypesForSave()
    await this.procoreIntegrationStore.saveConfiguration(
      undefined,
      undefined,
      workflowTypes,
    )
  }

  public areFieldSourcesEqual(
    source1: IPermitFieldSourceInput,
    source2: IPermitFieldSourceInput,
  ): boolean {
    return (
      (source1.permitFieldId &&
        source1.permitFieldId === source2.permitFieldId) ||
      (source1.permitSpecialField &&
        source1.permitSpecialField === source2.permitSpecialField)
    )
  }

  public handleToggleIntegration = () => {
    if (
      this.procoreIntegrationStore.isUserLoggedIn &&
      !this.procoreIntegrationStore.isSaving
    ) {
      this.procoreIntegrationStore.toggleIntegration(
        !this.procoreIntegrationStore.isIntegrationActive,
      )
    }
  }

  public handleLogin = () => {
    if (this.procoreIntegrationStore.isUserLoggedIn) {
      this.procoreIntegrationStore.logoutFromProcore()
    } else {
      this.procoreIntegrationStore.loginToProcore()
    }
  }

  public handleCompanySelect = (companyId: string) => {
    this.procoreIntegrationStore.setSelectedCompany(companyId)
    this.closeCompanyDropdown()
  }

  public getUserInitials(name: string): string {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  public get toggleTitle(): string {
    if (
      !this.procoreIntegrationStore.isUserLoggedIn &&
      !this.procoreIntegrationStore.isIntegrationActive
    )
      return Localization.translator.loginToEnableIntegration
    if (
      !this.procoreIntegrationStore.isUserLoggedIn &&
      this.procoreIntegrationStore.isIntegrationActive
    )
      return Localization.translator.loginToDisableIntegration
    return ''
  }

  public get loginBtnTitle(): string {
    if (this.procoreIntegrationStore.isInitializing)
      return Localization.translator.connecting

    return this.procoreIntegrationStore.isUserLoggedIn
      ? Localization.translator.logOut
      : Localization.translator.login
  }

  public toggleProjectSelection = (projectId: string) => {
    const { selectedCompanyId, isSaving, selectedProjects } =
      this.procoreIntegrationStore

    if (!selectedCompanyId || isSaving) return

    const currentIds = selectedProjects.map(p => p.id)

    const newSelectedProjectIds =
      this.procoreIntegrationStore.isProjectSelected(projectId)
        ? currentIds.filter(id => id !== projectId)
        : [...currentIds, projectId]

    this.procoreIntegrationStore.setSelectedProjects(
      newSelectedProjectIds,
      selectedCompanyId,
    )
  }

  public handleRemoveProject = (projectId: string) => {
    this.procoreIntegrationStore.removeProcoreProject(projectId)
  }

  public get availableProjectsForSelectedCompany(): IProcoreProject[] {
    if (!this.procoreIntegrationStore.selectedCompanyId) return []
    return this.procoreIntegrationStore.selectedCompany?.projects || []
  }

  private resolveWorkflowTypeId(workflowType: {
    typeId?: string
    initialType?: string
  }): { resolvedTypeId: string | undefined; wasStale: boolean } {
    const originalId = workflowType.typeId
    if (!originalId) return { resolvedTypeId: undefined, wasStale: false }

    if (workflowType.initialType) {
      const latest = this.workflowTypesStore?.getActualTypeByType(
        workflowType.initialType,
      )
      if (latest && latest.id !== originalId) {
        return { resolvedTypeId: latest.id, wasStale: true }
      }
    }
    return { resolvedTypeId: originalId, wasStale: false }
  }

  public resolveLatestPermitType(workflowTypeId: string): IPermitType | null {
    const directType =
      this.workflowTypesStore?.getPermitTypeById(workflowTypeId)
    return directType
      ? this.workflowTypesStore?.getActualTypeByType(directType.type) ||
          directType
      : null
  }

  public getWorkflowTypeFields(permitType: IPermitType): IWorkflowField[] {
    if (!permitType?.workflowSteps) return []

    const cacheKey = permitType.id
    if (cacheKey && this.workflowTypeFieldsCache.has(cacheKey)) {
      const cachedFields = this.workflowTypeFieldsCache.get(cacheKey)
      if (cachedFields) {
        return cachedFields
      }
    }

    const fields: IWorkflowField[] = []

    permitType.workflowSteps.forEach(
      (step: IWorkflowStep | null, stepIndex: number) => {
        if (!step?.fields) return

        step.fields.forEach((field: IPermitTypeField, fieldIndex: number) => {
          if (
            field.isShown &&
            field.caption &&
            !EXCLUDED_FIELD_TYPES.includes(field.type)
          ) {
            if (field.type === PermitFieldType.Table) {
              const tableColumns = this.getTableColumnFields(
                step,
                field,
                stepIndex + 1,
                fieldIndex + 1,
              )
              fields.push(...tableColumns)
            } else {
              fields.push({
                id: field.id,
                caption: field.caption,
                type: field.type,
                stepPosition: stepIndex + 1,
                fieldPosition: fieldIndex + 1,
              })
            }

            const conditionalFields = this.getConditionalFields(
              step,
              field,
              stepIndex + 1,
              fieldIndex + 1,
            )
            fields.push(...conditionalFields)
          }
        })
      },
    )

    fields.push(
      ...SPECIAL_FIELDS.map(field => ({
        id: field.id,
        caption: field.caption,
        type: null as PermitFieldType | null,
      })),
    )

    if (cacheKey) {
      this.workflowTypeFieldsCache.set(cacheKey, fields)
    }

    return fields
  }

  private getTableColumnFields(
    step: IWorkflowStep,
    tableField: IPermitTypeField,
    stepPosition: number,
    fieldPosition: number,
  ): IWorkflowField[] {
    const tableConditional = step.conditionalFields?.find(
      (cf: IConditionalField) =>
        cf.fieldId === tableField.id && cf.key === PERMIT_TABLE_FIELDS_KEY,
    )

    if (!tableConditional?.values || tableConditional.values.length === 0) {
      return []
    }

    const columnFields = tableConditional.values.map(
      (columnField: IPermitTypeField, subFieldIndex: number) => ({
        id: columnField.id,
        caption: columnField.caption,
        type: columnField.type,
        stepPosition,
        fieldPosition,
        subFieldPosition: subFieldIndex + 1,
        shouldIndent: true,
        parentFieldId: tableField.id,
      }),
    )

    return [
      {
        id: tableField.id,
        caption: tableField.caption,
        type: tableField.type,
        stepPosition,
        fieldPosition,
        isDisabled: true,
      },
      ...columnFields,
    ]
  }

  private getConditionalFields(
    step: IWorkflowStep,
    parentField: IPermitTypeField,
    stepPosition: number,
    fieldPosition: number,
    parentNestedPositions: number[] = [],
    depthLevel: number = 0,
  ): IWorkflowField[] {
    if (depthLevel >= MAX_CONDITIONAL_DEPTH_LEVEL) {
      return []
    }

    if (!step.conditionalFields) return []

    const filteredConditionals = step.conditionalFields.filter(
      (cf: IConditionalField) =>
        cf.fieldId === parentField.id && cf.key !== PERMIT_TABLE_FIELDS_KEY,
    )

    const fields: IWorkflowField[] = []

    filteredConditionals.forEach((conditional: IConditionalField) => {
      conditional.values?.forEach(
        (field: IPermitTypeField, subFieldIndex: number) => {
          if (
            field.isShown &&
            field.caption &&
            !EXCLUDED_FIELD_TYPES.includes(field.type)
          ) {
            const currentSubFieldPosition = subFieldIndex + 1
            const nestedPositions =
              depthLevel > 0
                ? [...parentNestedPositions, currentSubFieldPosition]
                : [currentSubFieldPosition]

            fields.push({
              id: field.id,
              caption: field.caption,
              type: field.type,
              stepPosition,
              fieldPosition,
              subFieldPosition:
                depthLevel === 0 ? currentSubFieldPosition : undefined,
              nestedSubFieldPositions: nestedPositions,
              shouldIndent: false,
              isConditional: true,
            })

            const nestedConditionals = this.getConditionalFields(
              step,
              field,
              stepPosition,
              fieldPosition,
              nestedPositions,
              depthLevel + 1,
            )
            fields.push(...nestedConditionals)
          }
        },
      )
    })

    return fields
  }

  private getSpecialFieldFromId(
    fieldId: string,
  ): SitePermitSpecialFieldType | null {
    const specialField = SPECIAL_FIELDS.find(f => f.id === fieldId)
    return specialField?.enumValue || null
  }

  private findPermitFieldInWorkflowSteps(
    permitType: IPermitType,
    fieldId: string,
  ): IPermitTypeField | undefined {
    if (!permitType?.workflowSteps) return undefined

    for (const step of permitType.workflowSteps) {
      if (!step) continue

      if (step.fields) {
        const fieldMatch = step.fields.find(
          (f: IPermitTypeField) => f.id === fieldId,
        )
        if (fieldMatch) return fieldMatch
      }

      if (step.conditionalFields) {
        for (const conditional of step.conditionalFields) {
          if (conditional.values) {
            const conditionalFieldMatch = conditional.values.find(
              (f: IPermitTypeField) => f.id === fieldId,
            )
            if (conditionalFieldMatch) return conditionalFieldMatch
          }
        }
      }
    }
    return undefined
  }

  public createFieldSource(
    workflowTypeId: string,
    field: IWorkflowField,
  ): IPermitFieldSourceInput | null {
    if (field.id.startsWith(SPECIAL_FIELD_PREFIX)) {
      const specialField = this.getSpecialFieldFromId(field.id)
      if (!specialField) return null
      return { permitSpecialField: specialField }
    }

    const permitType =
      this.workflowTypesStore?.getPermitTypeById(workflowTypeId)
    if (!permitType) return null

    const foundField = this.findPermitFieldInWorkflowSteps(permitType, field.id)
    if (!foundField) return null

    return {
      permitFieldId: foundField.id,
    }
  }

  public getSelectedFieldIdsFromMapping(
    selectedMapping: IPermitFieldSourceInput[] | undefined,
    workflowFields: IWorkflowField[],
  ): string[] {
    if (!selectedMapping || selectedMapping.length === 0) return []

    return selectedMapping
      .map(mapping => {
        if (mapping.permitSpecialField) {
          const specialField = SPECIAL_FIELDS.find(
            f => f.enumValue === mapping.permitSpecialField,
          )
          return specialField?.id || null
        } else if (mapping.permitFieldId) {
          const matchingField = workflowFields.find(
            f =>
              f.id === mapping.permitFieldId &&
              !f.id.startsWith(SPECIAL_FIELD_PREFIX),
          )
          return matchingField?.id || null
        }
        return null
      })
      .filter((id): id is string => id !== null)
  }

  @action.bound
  public async handleSelectAllFields(
    workflowTypeId: string,
    dailyLogFieldName: string,
    fields: IWorkflowField[],
    selectedFieldIds: string[],
  ) {
    const fieldSources: IPermitFieldSourceInput[] = []

    for (const field of fields) {
      if (field.isDisabled) continue
      if (!selectedFieldIds.includes(field.id)) {
        const fieldSource = this.createFieldSource(workflowTypeId, field)
        if (fieldSource) {
          fieldSources.push(fieldSource)
        }
      }
    }

    if (fieldSources.length === 0) return

    const currentMapping = this.getWorkflowTypeFieldMapping(
      workflowTypeId,
      dailyLogFieldName,
    )
    const existingSources = currentMapping || []
    const allSources = [...existingSources, ...fieldSources]

    await this.updateWorkflowTypeFieldMapping(
      workflowTypeId,
      dailyLogFieldName,
      allSources,
    )
  }

  @action.bound
  public async handleDeselectAllFields(
    workflowTypeId: string,
    dailyLogFieldName: string,
  ) {
    await this.updateWorkflowTypeFieldMapping(
      workflowTypeId,
      dailyLogFieldName,
      [],
    )
  }

  @action.bound
  public async handleSelectStepFields(
    workflowTypeId: string,
    dailyLogFieldName: string,
    stepPosition: number,
    fields: IWorkflowField[],
    selectedFieldIds: string[],
  ) {
    const fieldSources: IPermitFieldSourceInput[] = []

    for (const field of fields) {
      if (field.isDisabled) continue
      if (!selectedFieldIds.includes(field.id)) {
        const fieldSource = this.createFieldSource(workflowTypeId, field)
        if (fieldSource) {
          fieldSources.push(fieldSource)
        }
      }
    }

    if (fieldSources.length === 0) return

    const currentMapping = this.getWorkflowTypeFieldMapping(
      workflowTypeId,
      dailyLogFieldName,
    )
    const existingSources = currentMapping || []
    const allSources = [...existingSources, ...fieldSources]

    await this.updateWorkflowTypeFieldMapping(
      workflowTypeId,
      dailyLogFieldName,
      allSources,
    )
  }

  @action.bound
  public async handleDeselectStepFields(
    workflowTypeId: string,
    dailyLogFieldName: string,
    fields: IWorkflowField[],
    selectedFieldIds: string[],
  ) {
    const fieldsToRemove = fields.filter(f => selectedFieldIds.includes(f.id))
    const fieldSourcesToRemove: IPermitFieldSourceInput[] = []

    for (const field of fieldsToRemove) {
      const fieldSource = this.createFieldSource(workflowTypeId, field)
      if (fieldSource) {
        fieldSourcesToRemove.push(fieldSource)
      }
    }

    if (fieldSourcesToRemove.length === 0) return

    const currentMapping = this.getWorkflowTypeFieldMapping(
      workflowTypeId,
      dailyLogFieldName,
    )
    if (!currentMapping) return

    const remainingSources = currentMapping.filter(
      source =>
        !fieldSourcesToRemove.some(toRemove =>
          this.areFieldSourcesEqual(source, toRemove),
        ),
    )

    await this.updateWorkflowTypeFieldMapping(
      workflowTypeId,
      dailyLogFieldName,
      remainingSources,
    )
  }
}
